import React, { Fragment, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XIcon,
  PlusIcon,
  LightningBoltIcon,
  SparklesIcon,
  DocumentTextIcon,
  PlayIcon,
  UserCircleIcon,
} from "@heroicons/react/outline";
import Button from "../../UI/Button";
import { axiosPrivate } from "../../common/axiosPrivate";
import toast from "react-hot-toast";

const steps = [
  {
    icon: PlusIcon,
    title: "1. Add Your Content",
    text: "Easily bring in episodes by searching podcasts, connecting RSS feeds, adding YouTube links, or uploading your own audio/video files. Your content will appear in the Inbox.",
    videoLink: "https://podscribepub.s3.us-east-1.amazonaws.com/assets/intro_add_content.mp4",
  },
  {
    icon: LightningBoltIcon,
    title: "2. Unleash AI Insights",
    text: (
      <>
        Select an episode in your Inbox and click the <span className="font-semibold text-primary-700">Podscribe</span>{" "}
        button <LightningBoltIcon className="inline h-4 w-4 text-primary-500 -mt-1 mx-0.5" aria-hidden="true" />{" "}
        . Our AI gets to work transcribing, summarizing, and creating a mind map.
      </>
    ),
    videoLink: "https://podscribepub.s3.us-east-1.amazonaws.com/assets/intro_podscribe.mp4",
  },
  {
    icon: SparklesIcon,
    title: "3. Repurpose Instantly",
    text: "Once processed, head to the Create tab for your episode. Use our built-in recipes to instantly generate show notes, Twitter threads, blog outlines, emails, and more!",
    videoLink: "https://podscribepub.s3.us-east-1.amazonaws.com/assets/intro_create.mp4",
  },
  {
    icon: DocumentTextIcon,
    title: "4. Explore & Understand",
    text: "Dive deeper into your content anytime with AI summaries, searchable transcripts, interactive mind maps, and even ask questions using Ask AI.",
    videoLink: "https://podscribepub.s3.us-east-1.amazonaws.com/assets/intro_knowledge.mp4",
  },
  {
    icon: UserCircleIcon,
    title: "5. Tell Us About Yourself",
    text: "Your answers will help us tailor PodScribe.IO to your needs and make it even better. This is optional and will only take a moment.",
  },
];

const ROLES_OPTIONS = [
  "Podcaster / Audio Creator",
  "Marketer / Social Media Manager",
  "Content Agency / Freelancer",
  "Educator / Trainer",
  "Business Owner / Entrepreneur",
  "Researcher / Analyst",
  "Hobbyist / Personal Use",
  "Other",
];

const GOALS_OPTIONS = [
  "Repurpose content for social media (e.g., clips, posts)",
  "Create show notes, summaries, and transcripts",
  "Improve content discoverability (SEO, keywords)",
  "Analyze content for insights and trends",
  "Save time on post-production tasks",
  "Collaborate with a team on content creation",
  "Other",
];

export default function OnboardingModal({ show, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const [userRole, setUserRole] = useState("");
  const [otherRoleText, setOtherRoleText] = useState("");
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [otherGoalText, setOtherGoalText] = useState("");
  const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);

  const totalSteps = steps.length;
  const surveyStepIndex = totalSteps - 1;

  const handleCloseAndReset = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setCurrentStep(0);
    setIsVideoPlaying(false);
    setUserRole("");
    setOtherRoleText("");
    setPrimaryGoal("");
    setOtherGoalText("");
    setIsSubmittingSurvey(false);
    onClose();
  };

  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoPlaying(false);

    if (currentStep === surveyStepIndex) {
      console.log("User skipped survey and finished onboarding");
    } else {
      console.log("User skipped onboarding step/intro");
    }
    handleCloseAndReset();
  };

  const handleSubmitSurvey = async () => {
    if (isSubmittingSurvey) return;
    setIsSubmittingSurvey(true);

    let feedbackMessage = `Role: ${userRole}`;
    if (userRole === "Other" && otherRoleText) {
      feedbackMessage += ` (${otherRoleText})`;
    }
    feedbackMessage += `, Goal: ${primaryGoal}`;
    if (primaryGoal === "Other" && otherGoalText) {
      feedbackMessage += ` (${otherGoalText})`;
    }

    const payload = {
      emoji: "onboarding_survey",
      feedback: feedbackMessage,
    };

    try {
      await axiosPrivate.post("/users/feedback", payload);
      toast.success("Thanks for sharing!");
    } catch (error) {
      toast.error("Could not submit feedback. Please try again.");
    } finally {
      setIsSubmittingSurvey(false);
      handleCloseAndReset();
    }
  };

  const handleNext = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoPlaying(false);

    if (currentStep === surveyStepIndex) {
      handleSubmitSurvey();
    } else if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      console.log(`User completed onboarding step ${currentStep + 1}`);
    } else {
      console.log("User finished onboarding");
      handleCloseAndReset();
    }
  };

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const currentStepData = steps[currentStep];
  const CurrentIcon = currentStepData.icon;

  const isSurveyStep = currentStep === surveyStepIndex;

  let nextButtonText = "Next";
  if (isSurveyStep) {
    nextButtonText = isSubmittingSurvey ? "Submitting..." : "Submit & Get Started";
  }

  let skipButtonText = currentStep === 0 ? "Skip Intro" : "Skip Tutorial";
  if (isSurveyStep) {
    skipButtonText = "Skip & Finish";
  }

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-30" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:max-w-lg md:max-w-xl lg:max-w-3xl p-6 sm:p-8">
                <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
                  <button
                    type="button"
                    className="rounded-md bg-white text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={handleCloseAndReset}>
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="flex min-h-[380px] flex-col">
                  <div className="text-center mb-4">
                    {currentStep === 0 && (
                      <>
                        <Dialog.Title
                          as="h2"
                          className="text-xl sm:text-2xl font-semibold font-display text-neutral-900 mb-1">
                          Welcome to PodScribe.IO!
                        </Dialog.Title>
                        <p className="text-sm sm:text-base text-neutral-600">
                          Let's turn your audio & video content into valuable assets in just a few clicks. Here’s how:{" "}
                        </p>
                      </>
                    )}
                    {currentStep > 0 && currentStep < surveyStepIndex && (
                      <p className="text-sm font-medium text-primary-700">
                        Step {currentStep + 1} of {totalSteps - 1}
                      </p>
                    )}
                    {isSurveyStep && (
                      <p className="text-sm font-medium text-primary-700">Final Step - Optional</p>
                    )}
                  </div>

                  <div className="flex-grow mt-4 mb-6 flex flex-col items-center text-center">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                      <CurrentIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                    </div>

                    <Dialog.Title
                      as="h3"
                      className="text-lg sm:text-xl font-semibold text-neutral-900 font-display mb-2">
                      {isSurveyStep ? currentStepData.title : currentStepData.title.substring(currentStepData.title.indexOf(" ") + 1)}
                    </Dialog.Title>

                    <p className="text-sm sm:text-base text-neutral-700 max-w-md mx-auto">{currentStepData.text}</p>

                    {currentStepData.videoLink && !isSurveyStep && (
                      <div className="relative mt-5 w-full aspect-w-16 aspect-h-9 bg-neutral-100 rounded-md overflow-hidden border border-neutral-200">
                        {!isVideoPlaying && (
                          <button
                            type="button"
                            onClick={handlePlayVideo}
                            className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-50 transition-opacity duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-neutral-900 rounded-md"
                            aria-label="Play video">
                            <div className="bg-white/80 rounded-full p-3 hover:bg-white transition-colors duration-150">
                              <PlayIcon className="h-8 w-8 text-neutral-900" aria-hidden="true" />
                            </div>
                          </button>
                        )}
                        <video
                          ref={videoRef}
                          key={currentStepData.videoLink}
                          src={currentStepData.videoLink}
                          playsInline
                          onPlay={() => setIsVideoPlaying(true)}
                          onPause={() => setIsVideoPlaying(false)}
                          onEnded={() => setIsVideoPlaying(false)}
                          className="w-full h-full object-cover">
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}

                    {isSurveyStep && (
                      <div className="mt-6 w-full max-w-md text-left space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            What best describes you or your primary role?
                          </label>
                          <div className="space-y-2">
                            {ROLES_OPTIONS.map((role) => (
                              <div key={role} className="flex items-center">
                                <input
                                  id={`role-${role.replace(/\s+/g, "-")}`}
                                  name="userRole"
                                  type="radio"
                                  value={role}
                                  checked={userRole === role}
                                  onChange={(e) => setUserRole(e.target.value)}
                                  className="form-radio h-4 w-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                                />
                                <label htmlFor={`role-${role.replace(/\s+/g, "-")}`} className="ml-2 block text-sm text-neutral-800">
                                  {role}
                                </label>
                              </div>
                            ))}
                          </div>
                          {userRole === "Other" && (
                            <input
                              type="text"
                              value={otherRoleText}
                              onChange={(e) => setOtherRoleText(e.target.value)}
                              placeholder="Please specify"
                              className="mt-2 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            What's the main thing you hope to achieve with PodScribe.IO?
                          </label>
                          <div className="space-y-2">
                            {GOALS_OPTIONS.map((goal) => (
                              <div key={goal} className="flex items-center">
                                <input
                                  id={`goal-${goal.replace(/\s+/g, "-")}`}
                                  name="primaryGoal"
                                  type="radio"
                                  value={goal}
                                  checked={primaryGoal === goal}
                                  onChange={(e) => setPrimaryGoal(e.target.value)}
                                  className="form-radio h-4 w-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                                />
                                <label htmlFor={`goal-${goal.replace(/\s+/g, "-")}`} className="ml-2 block text-sm text-neutral-800">
                                  {goal}
                                </label>
                              </div>
                            ))}
                          </div>
                          {primaryGoal === "Other" && (
                            <input
                              type="text"
                              value={otherGoalText}
                              onChange={(e) => setOtherGoalText(e.target.value)}
                              placeholder="Please specify"
                              className="mt-2 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center space-x-2 my-5">
                    {steps.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (videoRef.current) videoRef.current.pause();
                          setIsVideoPlaying(false);
                          setCurrentStep(index);
                        }}
                        className={`h-2.5 w-2.5 rounded-full transition-colors duration-150 ${
                          currentStep === index ? "bg-primary-600" : "bg-neutral-300 hover:bg-primary-300"
                        } focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                        aria-label={`Go to step ${index + 1}`}
                        aria-current={currentStep === index ? "step" : undefined}
                      />
                    ))}
                  </div>

                  <div className="mt-auto flex flex-row items-center justify-between border-t border-neutral-200 pt-5">
                    <Button variant="tertiary" onClick={handleSkip} disabled={isSubmittingSurvey}>
                      {skipButtonText}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      disabled={
                        isSubmittingSurvey ||
                        (isSurveyStep &&
                          (!userRole ||
                            !primaryGoal ||
                            (userRole === "Other" && !otherRoleText) ||
                            (primaryGoal === "Other" && !otherGoalText)))
                      }>
                      {nextButtonText}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
